import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateTestCaseRequest,
  ExecutionStats,
  ExecutionSummary,
  SaveFullTestCaseRequest,
  SaveFullTestCaseResponse,
  TestCase,
  TestCaseActivity,
  TestCaseRevision,
  UpdateTestCaseRequest,
} from '../models/test-case.model';
import { Page } from '../models/page.model';

export type JiraRefreshType = 'story' | 'bug';

/** Seitengröße für die Historie-Liste; >100 Revisionen werden via Page.last signalisiert. */
export const REVISION_PAGE_SIZE = 100;

/** Seitengröße für den Aktivitäts-Feed; >100 Einträge werden via Page.last signalisiert. */
export const ACTIVITY_PAGE_SIZE = 100;

@Injectable({ providedIn: 'root' })
export class TestCaseService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/api/v1/projects`;

  /**
   * Testfaelle des Projekts. Mit `query` sucht der Server ueber Titel und
   * Beschreibung (NEX-1036) — clientseitiges Filtern wuerde die ganze Liste
   * uebertragen und die Beschreibung gar nicht kennen.
   */
  list(projectId: string, query?: string): Observable<TestCase[]> {
    const params = query?.trim() ? new HttpParams().set('query', query.trim()) : undefined;
    return this.http.get<TestCase[]>(`${this.baseUrl}/${projectId}/test-cases`, { params });
  }

  getById(projectId: string, id: string): Observable<TestCase> {
    return this.http.get<TestCase>(`${this.baseUrl}/${projectId}/test-cases/${id}`);
  }

  create(projectId: string, request: CreateTestCaseRequest): Observable<TestCase> {
    return this.http.post<TestCase>(`${this.baseUrl}/${projectId}/test-cases`, request);
  }

  update(projectId: string, id: string, request: UpdateTestCaseRequest): Observable<TestCase> {
    return this.http.patch<TestCase>(`${this.baseUrl}/${projectId}/test-cases/${id}`, request);
  }

  delete(projectId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/test-cases/${id}`);
  }

  createFull(projectId: string, request: SaveFullTestCaseRequest): Observable<SaveFullTestCaseResponse> {
    return this.http.post<SaveFullTestCaseResponse>(`${this.baseUrl}/${projectId}/test-cases/full`, request);
  }

  saveFull(projectId: string, id: string, request: SaveFullTestCaseRequest): Observable<SaveFullTestCaseResponse> {
    return this.http.put<SaveFullTestCaseResponse>(`${this.baseUrl}/${projectId}/test-cases/${id}/full`, request);
  }

  refreshJira(projectId: string, id: string, type: JiraRefreshType): Observable<TestCase> {
    const params = new HttpParams().set('type', type);
    return this.http.post<TestCase>(`${this.baseUrl}/${projectId}/test-cases/${id}/jira/refresh`, null, { params });
  }

  refreshFromRequirement(projectId: string, id: string): Observable<TestCase> {
    return this.http.post<TestCase>(`${this.baseUrl}/${projectId}/test-cases/${id}/refresh-from-requirement`, null);
  }

  getExecutionStats(projectId: string, id: string): Observable<ExecutionStats> {
    return this.http.get<ExecutionStats>(`${this.baseUrl}/${projectId}/test-cases/${id}/execution-stats`);
  }

  /** Projektweites Aggregat (Gesamt/Bestanden/Fehlgeschlagen/Pass-Rate) für den Kopfbereich (NEX-405). */
  getExecutionSummary(projectId: string): Observable<ExecutionSummary> {
    return this.http.get<ExecutionSummary>(`${this.baseUrl}/${projectId}/test-cases/execution-summary`);
  }

  getPreviousRevision(projectId: string, id: string): Observable<TestCaseRevision | null> {
    return this.http.get<TestCaseRevision | null>(`${this.baseUrl}/${projectId}/test-cases/${id}/revisions/previous`);
  }

  /**
   * Lädt die Versionshistorie eines Testfalls (neueste zuerst). Das Backend
   * paginiert; wir holen eine große Seite und geben die {@link Page} zurück,
   * damit die UI über {@code last} eine etwaige Kürzung signalisieren kann.
   */
  listRevisions(projectId: string, id: string): Observable<Page<TestCaseRevision>> {
    const params = new HttpParams().set('size', String(REVISION_PAGE_SIZE));
    return this.http.get<Page<TestCaseRevision>>(`${this.baseUrl}/${projectId}/test-cases/${id}/revisions`, {
      params,
    });
  }

  /**
   * Lädt den Aktivitäts-Feed eines Testfalls (neueste zuerst). Wie bei den
   * Revisionen wird eine große Seite geholt; {@code Page.last} signalisiert eine
   * etwaige Kürzung.
   */
  listActivities(projectId: string, id: string): Observable<Page<TestCaseActivity>> {
    const params = new HttpParams().set('size', String(ACTIVITY_PAGE_SIZE));
    return this.http.get<Page<TestCaseActivity>>(`${this.baseUrl}/${projectId}/test-cases/${id}/activity`, {
      params,
    });
  }
}
